import { useEffect } from 'react';

/**
 * Sets <title> and <meta> tags imperatively for SPA pages.
 * Restores the original page title on unmount.
 */
export default function useSeo({ title, description, keywords, image, url } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, attr = 'name') => {
      if (!content) return null;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const prev = el?.getAttribute('content') ?? null;
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return { el, prev, created };
    };

    const setCanonical = (href) => {
      if (!href) return null;
      let el = document.head.querySelector('link[rel="canonical"]');
      const prev = el?.getAttribute('href') ?? null;
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return { el, prev, created };
    };

    const restorers = [
      setMeta('description', description),
      setMeta('keywords', keywords),
      setMeta('og:title', title, 'property'),
      setMeta('og:description', description, 'property'),
      setMeta('og:image', image, 'property'),
      setMeta('og:url', url, 'property'),
      setMeta('twitter:card', image ? 'summary_large_image' : 'summary'),
      setMeta('twitter:title', title),
      setMeta('twitter:description', description),
      setMeta('twitter:image', image),
      setCanonical(url),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => {
        if (!r) return;
        if (r.created) r.el.remove();
        else if (r.prev !== null) r.el.setAttribute(r.el.tagName === 'LINK' ? 'href' : 'content', r.prev);
      });
    };
  }, [title, description, keywords, image, url]);
}
